import React, { useEffect, useRef, useCallback, useState } from "react";
import WithLoading from "components/Hoc/WithTransparentSpinner";
import { NotFound } from "components";
import CatalogHeader from "./components/catalog-header/";
import BrandCard from "./components/brand-card";
import CatalogProvider, { useCatalogContext } from "./CatalogContext";

const CatalogContent = ({ isAICatalog = true }) => {
  const {
    selectedFilters,
    loadingMore,
    fetchedInfo,
    loading,
    error,
    locationInfo,
    searchKeyword,
    fetchBrands,
    loadMoreBrands,
  } = useCatalogContext();

  const observerRef = useRef();
  const isInitialLoad = useRef(true);

  // Initial + search/filter fetch
  useEffect(() => {
    if (isInitialLoad.current) {
      fetchBrands({ isAICatalog: isAICatalog, });
      isInitialLoad.current = false;
    } else {
      fetchBrands({ isAICatalog: isAICatalog, searchKeyword, selectedFilters, locationInfo });
    }
  }, [isAICatalog, searchKeyword, selectedFilters, locationInfo, fetchBrands]);

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Intersection Observer for infinite loading
  const lastBrandElementRef = useCallback(
    (node) => {
      if (loading || loadingMore) return;
      if (!node) return;

      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries?.[0]?.isIntersecting) {
            const hasMorePages =
              fetchedInfo?.brands?.length <
              fetchedInfo?.metaData?.total_records;
            if (hasMorePages) {
              loadMoreBrands({ isAICatalog: isAICatalog });
            }
          }
        },
        { rootMargin: "300px" }
      );

      observerRef.current.observe(node);
    },
    [
      loading,
      loadingMore,
      fetchedInfo?.brands?.length,
      fetchedInfo?.metaData?.total_records,
      loadMoreBrands,
    ]
  );

  if (error) {
    return <NotFound message={error?.error?.message || "No brands found"} />;
  }

  return (
    <>
      <WithLoading loading={loading} />
      <div className="flex flex-col h-full store-page-popup">
        <CatalogHeader />

        {!loading && fetchedInfo?.brands?.length === 0 && (
          <div className="flex justify-center py-4">
            <NotFound message="No brands found" />
          </div>
        )}
        {/* Brands Grid */}
        <div className="p-4 flex flex-col gap-4">
          {fetchedInfo?.brands?.map((brand, index) => {
            const isLast = index === fetchedInfo.brands.length - 1;
            return (
              <div key={`brand-${brand?._id || index}-${index}`} ref={isLast ? lastBrandElementRef : null}>
                <BrandCard brand={brand} index={index} />
              </div>
            );
          })}

          {loadingMore && (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const Catalog = ({ isAICatalog = true }) => (
  <CatalogProvider>
    <CatalogContent isAICatalog={isAICatalog} />
  </CatalogProvider>
);

export default Catalog;
